/**
 * Unfunded staff — ACTIVE employees on seats carrying NO RF1 budget for the
 * year: the worker-grain inverse of the staged-items queue ("which of our
 * people has no funding line?"). Sessional roles (external markers,
 * invigilators, …) are expected to be unfunded (paid per-assignment from the
 * envelope pools) and are hidden by default; the RF1-eligible remainder is the
 * actionable reconciliation gap.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { budgetService, UnfundedEmployeeRow, UnfundedEmployeesResponse } from '@/services/api/organizationService';

const zar = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 });
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const UnfundedStaffView: React.FC<{ year: number }> = ({ year }) => {
  const [data, setData] = useState<UnfundedEmployeesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [includeSessional, setIncludeSessional] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    budgetService.getUnfundedEmployees(year)
      .then(setData)
      .catch(() => setError('Failed to load unfunded staff'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [year]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows || []).filter(r => {
      if (!includeSessional && r.sessional) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q)
        || r.incumbent.toLowerCase().includes(q)
        || r.pn.toLowerCase().includes(q)
        || r.position.toLowerCase().includes(q)
        || r.campus.toLowerCase().includes(q)
        || r.department.toLowerCase().includes(q)
        || r.employment.toLowerCase().includes(q)
      );
    });
  }, [data, search, includeSessional]);

  const visibleYtd = useMemo(() => rows.reduce((s, r) => s + r.ytd_ctc, 0), [rows]);

  const downloadCsv = () => {
    const header = ['Position', 'Title', 'Incumbent', 'PN', 'Employment', 'Campus', 'Department', 'Faculty', 'Sessional', 'YTD Actual (gross)', 'YTD Actual (CTC)'];
    const lines = rows.map(r => [
      r.position, `"${r.title.replace(/"/g, '""')}"`, `"${r.incumbent.replace(/"/g, '""')}"`, r.pn,
      r.employment, r.campus, r.department, r.faculty, r.sessional ? 'Yes' : '',
      String(r.ytd_actual), String(r.ytd_ctc),
    ].join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `unfunded_staff_${year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const asof = data?.asof_month ? MONTHS[data.asof_month - 1] : '';

  return (
    <Card>
      <CardContent className="p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-medium text-gray-700">
            Unfunded staff · {data ? `${data.rf1_count} RF1-eligible + ${data.sessional_count} sessional` : '…'}
          </div>
          <span className="text-xs text-gray-500">
            Active employees whose seat carries no RF1 budget — the people the reconciliation
            hasn&apos;t matched to any funding line. YTD spend to {asof}.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={includeSessional} onChange={e => setIncludeSessional(e.target.checked)} />
              Include sessional ({data?.sessional_count ?? 0})
            </label>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={cn('mr-1 h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!rows.length}>
              <Download className="mr-1 h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </div>
        <div className="relative max-w-sm">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Filter by name, PN, title, position, campus, department…"
            className="h-8 pl-7 text-xs" />
        </div>
        {loading ? (
          <div className="py-12 text-center text-gray-400"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />Loading…</div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-red-600">{error}</div>
        ) : (
          <div className="max-h-[62vh] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-800 text-white">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Position</th>
                  <th className="px-2 py-1.5 text-left font-medium">Title</th>
                  <th className="px-2 py-1.5 text-left font-medium">Incumbent</th>
                  <th className="px-2 py-1.5 text-left font-medium">PN</th>
                  <th className="px-2 py-1.5 text-left font-medium">Employment</th>
                  <th className="px-2 py-1.5 text-left font-medium">Campus</th>
                  <th className="px-2 py-1.5 text-left font-medium">Department</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Gross earnings − bonus/comm + employer contributions, Jan through the finalised month">YTD Actual (CTC)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.position}-${r.pn}-${i}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-2 py-1 tabular-nums">{r.position}</td>
                    <td className="max-w-[240px] truncate px-2 py-1">
                      {r.title}
                      {r.sessional && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">sessional</span>}
                    </td>
                    <td className="max-w-[180px] truncate px-2 py-1">{r.incumbent}</td>
                    <td className="px-2 py-1 tabular-nums">{r.pn}</td>
                    <td className="px-2 py-1">{r.employment}</td>
                    <td className="px-2 py-1">{r.campus || <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-1">{r.department || <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{zar.format(r.ytd_ctc)}</td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={8} className="px-2 py-10 text-center text-gray-400">No unfunded staff match this view.</td></tr>
                )}
              </tbody>
              {rows.length > 1 && (
                <tfoot className="font-semibold">
                  <tr>
                    <td className="sticky bottom-0 border-t bg-slate-100 px-2 py-1.5" colSpan={7}>
                      TOTAL · {rows.length} staff
                    </td>
                    <td className="sticky bottom-0 border-t bg-slate-100 px-2 py-1.5 text-right tabular-nums">{zar.format(visibleYtd)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UnfundedStaffView;
